// sanitize/input.ts
// Input validation and sanitization utilities

import {
  DEFAULT_SEARCH_QUERY_MAX_LENGTH,
  DEFAULT_ID_MAX_LENGTH,
  DEFAULT_ID_PATTERN,
  EMAIL_PATTERN,
  MAX_EMAIL_LENGTH
} from "./constants";
import type { SearchQuerySanitizeOptions, IdValidationOptions } from "./types";

// Re-export types for convenience
export type { SearchQuerySanitizeOptions, IdValidationOptions };

/**
 * Sanitizes search query input
 * Removes control characters and limits length to prevent DoS
 * @param query - Raw search query
 * @param options - Sanitization options
 * @returns Sanitized query string
 */
export const sanitizeSearchQuery = (
  query: string | undefined | null,
  options: SearchQuerySanitizeOptions = {}
): string => {
  const {
    maxLength = DEFAULT_SEARCH_QUERY_MAX_LENGTH,
    allowSpecialChars = false
  } = options;

  if (!query) return "";

  let sanitized = query
    .trim()
    // Remove control characters
    .replace(/[\x00-\x1F\x7F]/g, "");

  // Optionally remove special characters
  if (!allowSpecialChars) {
    // Keep alphanumeric, spaces, and common punctuation
    sanitized = sanitized.replace(/[^\w\s\-_.@]/g, "");
  }

  // Limit length to prevent DoS
  return sanitized.substring(0, maxLength);
};

/**
 * Validates an ID format (user ID, resource ID, etc.)
 * @param id - ID to validate
 * @param options - Validation options
 * @returns True if ID is valid
 */
export const isValidId = (
  id: string | undefined | null,
  options: IdValidationOptions = {}
): boolean => {
  const {
    pattern = DEFAULT_ID_PATTERN,
    maxLength = DEFAULT_ID_MAX_LENGTH
  } = options;

  if (!id || typeof id !== "string") return false;

  const trimmed = id.trim();

  if (trimmed.length === 0 || trimmed.length > maxLength) return false;

  return pattern.test(trimmed);
};

/**
 * Validates and sanitizes an ID
 * @param id - ID to validate
 * @param options - Validation options
 * @returns Sanitized ID or null if invalid
 */
export const sanitizeId = (
  id: string | undefined | null,
  options: IdValidationOptions = {}
): string | null => {
  if (!isValidId(id, options)) return null;
  return id!.trim();
};

/**
 * Validates an email address format
 * @param email - Email to validate
 * @returns True if email format is valid
 */
export const isValidEmail = (email: string | undefined | null): boolean => {
  if (!email || typeof email !== "string") return false;

  const trimmed = email.trim();

  return EMAIL_PATTERN.test(trimmed) && trimmed.length <= MAX_EMAIL_LENGTH;
};

/**
 * Sanitizes and validates an email address
 * @param email - Email to sanitize
 * @returns Sanitized email or null if invalid
 */
export const sanitizeEmail = (
  email: string | undefined | null
): string | null => {
  if (!isValidEmail(email)) return null;
  return email!.trim().toLowerCase();
};

/**
 * Validates a phone number format (basic validation)
 * @param phone - Phone number to validate
 * @returns True if phone format is valid
 */
export const isValidPhone = (phone: string | undefined | null): boolean => {
  if (!phone || typeof phone !== "string") return false;

  const trimmed = phone.trim();

  // Allow digits, spaces, hyphens, parentheses, and plus sign
  const phonePattern = /^[\d\s\-()+ ]{7,20}$/;

  return phonePattern.test(trimmed);
};

/**
 * Sanitizes a phone number (removes non-essential characters)
 * @param phone - Phone number to sanitize
 * @returns Sanitized phone number or null if invalid
 */
export const sanitizePhone = (
  phone: string | undefined | null
): string | null => {
  if (!isValidPhone(phone)) return null;
  
  // Keep only digits and plus sign
  return phone!.replace(/[^\d+]/g, "");
};

/**
 * Validates a numeric string
 * @param value - Value to validate
 * @param options - Validation options
 * @returns True if value is a valid number
 */
export const isValidNumeric = (
  value: string | undefined | null,
  options: { allowDecimal?: boolean; allowNegative?: boolean } = {}
): boolean => {
  const { allowDecimal = true, allowNegative = true } = options;

  if (!value || typeof value !== "string") return false;

  const trimmed = value.trim();

  let pattern = "^";
  if (allowNegative) pattern += "-?";
  pattern += "\\d+";
  if (allowDecimal) pattern += "(\\.\\d+)?";
  pattern += "$";

  return new RegExp(pattern).test(trimmed);
};

/**
 * Sanitizes a string to contain only alphanumeric characters
 * @param value - Value to sanitize
 * @returns Alphanumeric-only string
 */
export const sanitizeAlphanumeric = (
  value: string | undefined | null
): string => {
  if (!value) return "";
  return value.replace(/[^a-zA-Z0-9]/g, "");
};

/**
 * Sanitizes a string to contain only letters
 * @param value - Value to sanitize
 * @returns Letters-only string
 */
export const sanitizeLettersOnly = (
  value: string | undefined | null
): string => {
  if (!value) return "";
  return value.replace(/[^a-zA-Z]/g, "");
};

/**
 * Sanitizes a string to contain only digits
 * @param value - Value to sanitize
 * @returns Digits-only string
 */
export const sanitizeDigitsOnly = (
  value: string | undefined | null
): string => {
  if (!value) return "";
  return value.replace(/[^\d]/g, "");
};